/**
 * Hearts-fly effect on green CTA buttons.
 *
 * Draws a burst of hearts on a single full-screen <canvas> and animates them
 * via requestAnimationFrame. A single canvas (rather than dozens of animated
 * DOM nodes) keeps the animation on the compositor and avoids the per-element
 * layer/repaint cost that lagged in the installed PWA.
 */
// Hearts-fly is a brand feature — only tenants whose brand opts in
// (`brand.features.hearts`) get it. The script is only emitted for them, and this
// guard means it stays inert even if it is ever loaded elsewhere.
function heartsEnabled() {
  try {
    const brand = JSON.parse(document.body.dataset.brand || '{}');
    return !!(brand.features && brand.features.hearts);
  } catch {
    return false;
  }
}

function initHearts() {
  if (!heartsEnabled()) return;
  const buttons = document.querySelectorAll('[data-hearts]:not([data-hearts-initialized])');
  if (!buttons.length) return;

  buttons.forEach(button => {
    button.dataset.heartsInitialized = 'true';

    button.addEventListener('click', () => {
      const rect = button.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      const icon = button.querySelector('svg');
      if (icon) {
        icon.classList.remove('heart-beat');
        void icon.offsetWidth;
        icon.classList.add('heart-beat');
        icon.addEventListener('animationend', () => icon.classList.remove('heart-beat'), { once: true });
      }

      const isSticky = !!button.closest('[data-sticky-heart]');
      const isRtl = document.documentElement.dir === 'rtl';
      // Defer the (canvas-allocating) burst one frame so the button's own press
      // feedback paints first — otherwise the click feels laggy.
      requestAnimationFrame(() => spawnHearts(cx, cy, isSticky, isRtl));
    });
  });
}

function spawnHearts(cx, cy, biased, flipX) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = document.createElement('canvas');
  canvas.className = 'hearts-canvas';
  canvas.width = Math.round(window.innerWidth * dpr);
  canvas.height = Math.round(window.innerHeight * dpr);
  canvas.style.width = window.innerWidth + 'px';
  canvas.style.height = window.innerHeight + 'px';
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  const range = Math.max(window.innerWidth, window.innerHeight) * 1.2;
  const dir = flipX ? -1 : 1;
  const particles = [];

  for (let i = 0; i < 34; i++) {
    const size = 6 + Math.random() * 24;
    const tx = biased ? Math.random() * range * 0.6 * dir : (Math.random() - 0.5) * range;
    const ty = biased ? (Math.random() - 0.5) * range * 0.8 : (Math.random() - 0.5) * range;
    const rz = Math.random() * 480;
    const delay = Math.random() * 240;
    const duration = 2800 + Math.random() * 2000;
    const hue = 330 + Math.random() * 40;
    const sat = 60 + Math.random() * 40;
    const lit = 50 + Math.random() * 30;
    particles.push({ cx, cy, tx, ty, rz, delay, duration, size, color: `hsl(${hue} ${sat}% ${lit}%)`, t: 0, started: false });
  }

  let last = performance.now();
  let raf = 0;

  function frame(now) {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

    let alive = false;
    for (const p of particles) {
      if (!p.started) {
        p.delay -= dt * 1000;
        if (p.delay > 0) continue;
        p.started = true;
      }
      p.t += dt * 1000;
      if (p.t >= p.duration) continue;
      alive = true;

      const k = p.t / p.duration;
      const ease = 1 - Math.pow(1 - k, 3); // easeOutCubic
      const x = p.cx + p.tx * ease;
      const y = p.cy + p.ty * ease;
      const rot = -45 + p.rz * ease;
      const alpha = 1 - k;
      drawHeart(ctx, x, y, p.size * (1 + k * 0.5), rot, alpha, p.color);
    }

    if (alive) {
      raf = requestAnimationFrame(frame);
    } else {
      cancelAnimationFrame(raf);
      canvas.remove();
    }
  }

  raf = requestAnimationFrame(frame);
}

function drawHeart(ctx, x, y, size, rotation, alpha, color) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(size, size);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, 0.42);
  ctx.bezierCurveTo(0, 0.42, -0.55, 0.05, -0.55, -0.2);
  ctx.bezierCurveTo(-0.55, -0.55, -0.1, -0.55, 0, -0.18);
  ctx.bezierCurveTo(0.1, -0.55, 0.55, -0.55, 0.55, -0.2);
  ctx.bezierCurveTo(0.55, 0.05, 0, 0.42, 0, 0.42);
  ctx.fill();
  ctx.restore();
}

document.addEventListener('page:load', initHearts);
